import { useCallback, useEffect, useState } from "react";
import { CopyCheck, Link2, Split } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RepartitionDialog } from "@/components/RepartitionDialog";
import { ReservationDetailDialog } from "@/components/ReservationDetailDialog";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CompteSelect } from "@/components/CompteSelect";
import { LoyerOperation } from "@/components/LoyerOperation";
import { LogementItems } from "@/components/LogementItems";
import { DoublonBouton } from "@/components/DoublonLien";
import { api, errMsg, money } from "@/lib/api";

const AUCUN = "__aucun__";
const FONDS = ["bg-slate-50/80", "bg-emerald-50/50"];
const Lbl = ({ children }) => <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500 w-36 shrink-0">{children}</span>;
const Memo = ({ show }) => (show ? <span className="text-[10px] font-mono text-emerald-700 shrink-0" title="Repris d'une opération identique d'un autre mois">mémorisé</span> : null);

const TaxeInput = ({ op, k, save, testId }) => {
  const [v, setV] = useState(String(op[k] ?? ""));
  useEffect(() => setV(String(op[k] ?? "")), [op, k]);
  const fige = op.reparti || op.non_taxable || op.non_creditable || (op.creditable && !op.creditable[k]) || (op.taxe_seule && op.taxe_seule !== k);
  const blur = () => {
    if (v.trim() === "") return !op[`${k}_auto`] && save({ [k]: null });
    const n = Math.abs(Number(v.replace(",", ".")));
    if (Number.isNaN(n)) return setV(String(op[k] ?? ""));
    if (op[`${k}_auto`] || n !== Math.abs(op[k])) save({ [k]: n });
  };
  return (
    <div className="flex items-center gap-1 justify-end">
      <span className="sm:hidden text-[10px] font-mono text-slate-500 uppercase">{k}</span>
      <Input value={v} onChange={(e) => setV(e.target.value)} onBlur={blur} inputMode="decimal" disabled={fige}
        className={`h-8 w-24 text-right font-mono text-xs ${op[`${k}_auto`] ? "text-slate-500 italic bg-white/70" : "text-slate-900 border-emerald-500 bg-white"} ${op[k] < 0 ? "text-red-700" : ""}`} data-testid={testId} />
    </div>
  );
};

const CaseTaxe = ({ label, checked, onChange, testId, memo }) => (
  <label className="flex items-center justify-end gap-1.5 whitespace-nowrap text-[10px] font-mono uppercase text-slate-500">
    {label}<Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="bg-white" data-testid={testId} />
    {memo && <span className="shrink-0 font-mono text-[10px] text-emerald-700" title="Repris d'une opération identique d'un autre mois"><span className="hidden sm:inline">mémorisé</span><span className="sm:hidden">●</span></span>}
  </label>
);

const OptionsTaxes = ({ op, save, t }) => (
  <div className="col-start-3 flex flex-wrap justify-end gap-x-3 gap-y-1 sm:col-start-auto sm:flex-col sm:items-end">
    <CaseTaxe label="Non taxable" checked={!!op.non_taxable} memo={op.non_taxable_memo} testId={`${t}-non-taxable`}
      onChange={(v) => save(v ? { non_taxable: true, taxable: false, taxe_seule: "" } : { non_taxable: false })} />
    {((op.taxabilite && op.taxabilite !== "tps_tvq") || op.taxable) && !op.reparti && (
      <CaseTaxe label="Taxable" checked={!!op.taxable && !op.non_taxable} memo={op.taxable_memo} testId={`${t}-taxable`}
        onChange={(v) => save(v ? { taxable: true, non_taxable: false } : { taxable: false })} />
    )}
    {["tps", "tvq"].map((k) => (
      <CaseTaxe key={k} label={`${k.toUpperCase()} seul.`} checked={op.taxe_seule === k && !op.non_taxable} memo={op.taxe_seule === k && op.taxe_seule_memo}
        testId={`${t}-${k}-seulement`} onChange={(v) => save(v ? { taxe_seule: k, non_taxable: false } : { taxe_seule: "" })} />
    ))}
  </div>
);

export const PetitSelect = ({ value, options, onChange, placeholder, memo, testId, parImmeuble, avecImmeuble }) => (
  <Select value={value || AUCUN} onValueChange={(v) => onChange(v === AUCUN ? "" : v)}>
    <SelectTrigger className={`h-9 bg-white ${memo ? "italic text-slate-600" : ""}`} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={AUCUN}>{placeholder}</SelectItem>
      {parImmeuble ? <LogementItems logements={options} avecImmeuble={avecImmeuble} /> : options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

const MOIS_C = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
export const jour = (d) => (d ? `${Number(d.slice(8, 10))} ${MOIS_C[Number(d.slice(5, 7)) - 1]}` : "");

// Pastille d'une réservation : code · client · dates · logement
export const ResaPastille = ({ r, onClick, testId }) => (
  <button type="button" onClick={onClick} title="Voir le détail de la réservation" className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-left hover:border-emerald-400 hover:bg-emerald-50 transition-colors" data-testid={testId}>
    <span className="font-mono font-semibold text-slate-800 underline decoration-dotted underline-offset-2">{r.code}</span>
    {r.client && <span className="text-slate-600"> · {r.client}</span>}
    {r.debut && <span className="text-slate-500"> · {jour(r.debut)} → {jour(r.fin)}</span>}
    {r.logement && <span className="whitespace-nowrap text-emerald-700"> · {r.logement}</span>}
  </button>
);

const Reservations = ({ op, t }) => {
  const [rid, setRid] = useState(null);
  const fermer = useCallback(() => setRid(null), []);
  return (
  <div className="mt-1.5 flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3" data-testid={`${t}-reservations`}>
    <Lbl>{op.reservations.length > 1 ? `Réservations (${op.reservations.length})` : "Réservation"}</Lbl>
    <div className="flex flex-wrap gap-1.5">
      {op.reservations.map((r, i) => (
        <ResaPastille key={`${r.code}-${i}`} r={r} onClick={() => setRid(r.id)} testId={`${t}-reservation-${i}`} />
      ))}
    </div>
    {rid && <ReservationDetailDialog rid={rid} onClose={fermer} />}
  </div>
  );
};

const Parts = ({ op, onOpen, t }) => (
  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs" data-testid={`${t}-parts`}>
    <Lbl>Répartition</Lbl>
    {op.parts.map((p) => (
      <span key={p.immeuble} className="inline-flex items-center gap-1 rounded border border-sky-200 bg-white px-2 py-1 font-mono" title={`Part de l'opération de ${money(op.debit || op.credit)}`} data-testid={`${t}-part-${p.immeuble}`}>
        <Link2 className="h-3 w-3 text-sky-600" />{p.immeuble} {money(p.montant)} <span className="text-slate-400">sur {money(op.debit || op.credit)}</span> · TPS {money(Math.abs(p.tps || p.tps_nc))} · TVQ {money(Math.abs(p.tvq || p.tvq_nc))}{p.tps_nc || p.tvq_nc ? " (non créd.)" : ""}
      </span>
    ))}
    <button type="button" onClick={onOpen} className="text-emerald-700 hover:underline" data-testid={`${t}-parts-modifier`}>Modifier</button>
  </div>
);

const Chaine = ({ op }) => (op.reparti ? <Link2 className="inline h-3.5 w-3.5 mr-1 text-sky-600" aria-label="Montant réparti" /> : null);

export const OperationBlock = ({ op, index, comptes, categories, logements, onSaved, coche, onCoche, nbSemblables, onSemblables }) => {
  const [commentaire, setCommentaire] = useState(op.commentaire || "");
  const [repartir, setRepartir] = useState(false);
  useEffect(() => setCommentaire(op.commentaire || ""), [op.commentaire]);
  const save = async (champs) => {
    try { await api.put(`/transactions/${op.id}/commentaire`, champs); onSaved(); toast.success("Enregistré et mémorisé"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const t = `op-${op.id}`;
  return (
    <div className={`mb-3 rounded-lg border px-3 py-3 shadow-sm transition-colors ${coche ? "border-amber-300 bg-amber-50 ring-1 ring-inset ring-amber-300" : `border-slate-300 ${FONDS[index % 2]}`} ${op.doublon ? "opacity-60" : ""}`} data-testid={t}>
      <div className="grid grid-cols-[110px_44px_1fr] sm:grid-cols-[124px_52px_1fr_110px_110px_100px_100px_112px] gap-x-3 gap-y-1 items-center text-sm">
        <label className="flex items-center gap-2 font-mono text-xs text-slate-600">
          <Checkbox checked={coche} onCheckedChange={(v) => onCoche(op.id, !!v)} className="bg-white" data-testid={`${t}-select`} />{op.date}
        </label>
        <span className="font-mono text-xs text-center rounded bg-white py-0.5" data-testid={`${t}-rang`}>{op.rang}</span>
        <span className="text-slate-900">{op.description}
          {nbSemblables > 1 && (
            <button type="button" onClick={() => onSemblables(op.cle)} className="ml-2 inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-mono text-slate-600 hover:border-emerald-600 hover:text-emerald-700 transition-colors" data-testid={`${t}-semblables`}>
              <CopyCheck className="h-3 w-3" />Cocher les {nbSemblables} semblables
            </button>
          )}
        </span>
        <span className="font-mono text-right text-red-700 col-start-3 sm:col-start-auto" data-testid={`${t}-retrait`} title={op.reparti && op.debit ? `Réparti : ${op.parts.map((p) => `${p.immeuble} ${money(p.montant)}`).join(" · ")}` : undefined}>{op.debit ? <><Chaine op={op} />− {money(op.debit)}</> : ""}</span>
        <span className="font-mono text-right text-emerald-700 col-start-3 sm:col-start-auto" data-testid={`${t}-depot`} title={op.reparti && op.credit ? `Réparti : ${op.parts.map((p) => `${p.immeuble} ${money(p.montant)}`).join(" · ")}` : undefined}>{op.credit ? <><Chaine op={op} />{money(op.credit)}</> : ""}</span>
        <div className="col-start-3 sm:col-start-auto"><TaxeInput op={op} k="tps" save={save} testId={`${t}-tps`} /></div>
        <div className="col-start-3 sm:col-start-auto"><TaxeInput op={op} k="tvq" save={save} testId={`${t}-tvq`} /></div>
        <OptionsTaxes op={op} save={save} t={t} />
      </div>
      <div className="mt-2 flex flex-col lg:flex-row lg:items-center gap-1 lg:gap-3">
        <Lbl>Compte comptable</Lbl>
        <div className="flex-1 min-w-0 flex items-center gap-2"><CompteSelect value={op.compte || ""} comptes={comptes} memo={op.compte_memo} onChange={(compte) => save({ compte })} testId={`${t}-compte`} /><Memo show={op.compte_memo} /></div>
        <div className="lg:w-56 flex items-center gap-2"><PetitSelect value={op.categorie} options={categories} placeholder="Catégorie" memo={op.categorie_auto || op.categorie_memo} onChange={(categorie) => save({ categorie })} testId={`${t}-categorie`} /></div>
        <div className="lg:w-64 flex items-center gap-2">{op.reparti
          ? <span className="h-9 w-full flex items-center rounded-md border border-emerald-200 bg-white px-3 text-sm text-emerald-800" data-testid={`${t}-logement-reparti`}>Réparti entre {op.parts.length} immeubles</span>
          : <PetitSelect parImmeuble avecImmeuble value={op.logement} options={logements} placeholder={op.immeuble ? `Immeuble : ${op.immeuble}` : "Immeuble / logement"} memo={op.logement_memo || op.logement_deduit} onChange={(logement) => save({ logement })} testId={`${t}-logement`} />}</div>
        <Button size="sm" variant="outline" className="h-9 bg-white" onClick={() => setRepartir(true)} title="Répartir cette opération entre plusieurs immeubles" data-testid={`${t}-repartir`}><Split className="h-3.5 w-3.5 mr-1" />Répartir</Button>
        {!op.reparti && <DoublonBouton op={op} onChange={() => onSaved()} testId={`${t}-doublon`} />}
      </div>
      {op.parts?.length > 0 && <Parts op={op} onOpen={() => setRepartir(true)} t={t} />}
      {op.reservations?.length > 0 && <Reservations op={op} t={t} />}
      {repartir && <RepartitionDialog op={op} open={repartir} onClose={() => setRepartir(false)} onSaved={onSaved} />}
      {op.credit > 0 && !op.plateforme && <LoyerOperation op={op} onSaved={onSaved} />}
      <div className="mt-1.5 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-3">
        <Lbl>Commentaire</Lbl>
        {op.taxes_note && <span className={`text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 ${op.reparti ? "sm:basis-full sm:order-last" : "shrink-0"}`} data-testid={`${t}-taxes-note`}>{op.taxes_note}</span>}
        <Input value={commentaire} onChange={(e) => setCommentaire(e.target.value)} placeholder="Ajouter un commentaire…" className={`h-9 bg-white sm:flex-1 sm:min-w-[200px] ${op.commentaire_memo ? "italic text-slate-600" : ""}`}
          onBlur={() => commentaire.trim() !== (op.commentaire || "") && save({ commentaire })} data-testid={`${t}-commentaire`} />
      </div>
    </div>
  );
};
